import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Text, Icon, Button, Input, Modal, FormAlert } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import { DevProject } from '../../types/chat';
import { IfHasPermission } from '../IfHasPermission';
import { Permissions } from '../../constants';
import mcpApi, { MASKED_SECRET, MCPServer, MCPTestConnectionResult } from '../../services/mcpApi';
import { extractErrorCode, extractErrorDetail } from './types';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { StatusBadge } from './StatusBadge';
import { EnableToggle } from './EnableToggle';

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex flex-col gap-[2px] w-full">
    <Text variant="text-xs" weight="medium" color="tertiary">
      {label}
    </Text>
    {children}
  </div>
);

interface ServerDetailsViewProps {
  server: MCPServer;
  testing: boolean;
  deleting: boolean;
  isBuilding: boolean;
  onTest: () => Promise<MCPTestConnectionResult | null>;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onRefresh: () => Promise<void>;
  project: DevProject | null;
}

/** Read-only detail screen for one server + its Test/Edit/Toggle/Delete actions. */
export const ServerDetailsView: React.FC<ServerDetailsViewProps> = ({
  server,
  testing,
  deleting,
  isBuilding,
  onTest,
  onEdit,
  onToggle,
  onDelete,
  onRefresh,
  project,
}) => {
  const [oauthBusy, setOauthBusy] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  // Popup asking for a pre-registered client ID + secret — opened only when the
  // provider doesn't support automatic registration (DCR) on Sign in.
  const [credsOpen, setCredsOpen] = useState(false);
  const [credsClientId, setCredsClientId] = useState('');
  const [credsClientSecret, setCredsClientSecret] = useState('');
  // Space/comma-separated scopes; blank = request everything the server advertises.
  const [credsScopes, setCredsScopes] = useState('');
  const [credsError, setCredsError] = useState<string | null>(null);
  const [credsSaving, setCredsSaving] = useState(false);

  const isIndividual = server.sharing_mode === 'individual';

  // The test result is persisted server-side; after onTest() the refreshed
  // status badge, tools list and error block below reflect it.
  const runTest = async () => {
    await onTest();
  };

  const isOAuth = !!server.oauth;
  // Stored tokens don't count as authorized when the server rejected them
  // (auth_required) or the last check failed (last_error present).
  const authorized =
    !!server.oauth?.has_oauth_tokens && server.status !== 'auth_required' && !server.last_error;

  const signIn = async () => {
    setOauthError(null);
    setOauthBusy(true);
    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      window.removeEventListener('focus', onFocus);
      setOauthBusy(false);
    };
    // Fast path: the callback popup reports the outcome via postMessage. COOP
    // providers (e.g. Sentry) sever the popup↔opener link so this never fires
    // for them — onFocus below is the provider-independent completion signal.
    const onMessage = (e: MessageEvent) => {
      if (e.data && e.data.source === 'mcp-oauth') {
        cleanup();
        if (!e.data.success) setOauthError(e.data.message || 'Sign-in failed.');
        void onRefresh();
      }
    };
    // The popup closing always hands focus back to this window (no provider
    // header can prevent that), so refetch the status whenever focus returns
    // while the sign-in is pending. Focus can also return mid-login (user
    // clicks this window), so a single focus is not treated as completion —
    // listeners stay until the outcome message or the safety net.
    const onFocus = () => {
      setOauthBusy(false);
      void onRefresh();
    };
    try {
      const { authorization_url } = await mcpApi.startOAuth(server.project_id, server.id);
      window.addEventListener('message', onMessage);
      window.addEventListener('focus', onFocus);
      window.open(authorization_url, 'mcp-oauth-signin', 'width=620,height=820');
      // Safety net so the listeners never leak if the flow is abandoned.
      setTimeout(cleanup, 5 * 60 * 1000);
    } catch (e: any) {
      cleanup();
      if (extractErrorCode(e) === 'manual_client_required') {
        // Provider has no DCR — collect a pre-registered client ID + secret.
        setCredsError(null);
        setCredsOpen(true);
      } else {
        setOauthError(extractErrorDetail(e, 'Failed to start sign-in.'));
      }
    }
  };

  const submitClientCredentials = async () => {
    const clientId = credsClientId.trim();
    if (!clientId) {
      setCredsError('Client ID is required.');
      return;
    }
    try {
      setCredsSaving(true);
      setCredsError(null);
      await mcpApi.updateServer(server.project_id, server.id, {
        oauth: {
          client_id: clientId,
          ...(credsClientSecret ? { client_secret: credsClientSecret } : {}),
          // Blank = request everything the server advertises (auto-discovered on
          // Sign in); an explicit list narrows the request to least-privilege.
          scopes: credsScopes.split(/[\s,]+/).filter(Boolean),
        },
      });
      setCredsOpen(false);
      setCredsClientId('');
      setCredsClientSecret('');
      setCredsScopes('');
      await onRefresh();
      // Continue straight into the provider consent popup with the new client.
      await signIn();
    } catch (e: any) {
      setCredsError(extractErrorDetail(e, 'Failed to save client credentials.'));
    } finally {
      setCredsSaving(false);
    }
  };

  const headerNames = Object.keys(server.custom_headers || {});

  const busy = testing || deleting;
  // Edit / delete / toggle mutate config and invalidate the pooled runtime
  // client — never allow them mid-build.
  const mutationsLocked = busy || isBuilding;

  const oauthSignInBlock = (
    <div className="flex flex-col gap-bg-v2-xs p-bg-v2-md bg-bg-secondary border border-border-secondary rounded-bg-v2-md">
      <div className="flex items-center justify-between gap-bg-v2-md">
        <div className="flex items-center gap-bg-v2-xs min-w-0">
          <Icon
            name={authorized ? IconName.CheckCircle : IconName.Lock}
            size="sm"
            className={authorized ? 'text-utility-success-700 shrink-0' : 'text-text-tertiary shrink-0'}
            inheritColor
          />
          <Text variant="text-xs" color="secondary" className="min-w-0 break-words">
            {authorized ? 'Authorized via OAuth' : 'Not authorized yet'}
            {server.oauth?.expires_at && authorized
              ? ` · expires ${formatDateTime(server.oauth.expires_at)}`
              : ''}
          </Text>
        </div>
        <Button
          variant="primary"
          size="small"
          onClick={signIn}
          disabled={oauthBusy || mutationsLocked}
          loading={oauthBusy}
          title={isBuilding ? 'Unavailable while a build is running' : undefined}
          className="shrink-0"
          label={
            oauthBusy ? (
              'Waiting…'
            ) : (
              <div className="flex items-center gap-bg-v2-xs">
                <Icon name={IconName.ExternalLink} size="xs" inheritColor />
                <span>{authorized ? 'Reconnect' : 'Sign in'}</span>
              </div>
            )
          }
        />
      </div>
      {oauthError && (
        <Text variant="text-xs" color="error" className="break-words">
          {oauthError}
        </Text>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-[var(--spacing-lg,12px)] w-full">
      <div className="flex items-center gap-[8px]">
        <div className="flex items-center gap-[8px] flex-wrap flex-1 min-w-0">
          <StatusBadge status={server.status} testing={testing} />
          <span className="bg-bg-secondary border border-border-secondary text-text-tertiary px-2 py-0.5 rounded-full text-bg-v2-xs font-bg-v2-medium whitespace-nowrap">
            {isIndividual ? 'Individual' : 'Shared'}
          </span>
          {!server.enabled && (
            <span className="bg-bg-secondary border border-border-secondary text-text-tertiary px-2 py-0.5 rounded-full text-bg-v2-xs font-bg-v2-medium whitespace-nowrap">
              Disabled
            </span>
          )}
        </div>

        {/* Tiny actions — right of the badge */}
        <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
          <div className="flex items-center gap-[var(--spacing-xs,4px)] shrink-0">
            <button
              onClick={runTest}
              disabled={busy}
              title={server.status === 'auth_required' ? 'Test after updating credentials' : 'Test connection'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
            >
              <Icon name={IconName.Reload} size="xs" spin={testing} className="text-text-tertiary" inheritColor />
            </button>
            <button
              onClick={onEdit}
              disabled={mutationsLocked}
              title={
                isBuilding
                  ? 'Unavailable while a build is running'
                  : server.status === 'auth_required'
                  ? 'Update credentials'
                  : 'Edit'
              }
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-secondary-hover transition-colors disabled:opacity-50"
            >
              <Icon name={IconName.Edit} size="xs" className="text-text-tertiary" inheritColor />
            </button>
            <button
              onClick={onDelete}
              disabled={mutationsLocked}
              title={isBuilding ? 'Unavailable while a build is running' : 'Delete server'}
              className="flex items-center justify-center w-[28px] h-[28px] rounded-full hover:bg-bg-error-primary transition-colors disabled:opacity-50"
            >
              <Icon name={deleting ? IconName.Loading : IconName.Delete} size="xs" spin={deleting} className="text-text-tertiary hover:text-text-error-primary transition-colors" inheritColor />
            </button>
          </div>
        </IfHasPermission>
      </div>

      {server.last_error && server.status !== 'connected' && (
        <div className="flex items-start gap-[var(--spacing-xs,4px)] p-[var(--spacing-md,8px)] bg-bg-error-primary border border-border-error-subtle rounded-[var(--radius-sm,6px)]">
          <Icon name={IconName.Alert} size="sm" className="text-text-error-primary shrink-0 mt-[2px]" inheritColor />
          <Text variant="text-xs" color="error" className="break-words">
            {server.last_error}
          </Text>
        </div>
      )}

      {isOAuth &&
        (isIndividual ? (
          oauthSignInBlock
        ) : (
          <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
            {oauthSignInBlock}
          </IfHasPermission>
        ))}

      <DetailRow label="URL">
        <Text variant="text-sm" color="primary" className="font-mono break-all">
          {server.url}
        </Text>
      </DetailRow>

      <DetailRow label="Transport">
        <Text variant="text-sm" color="primary">
          {server.transport === 'sse' ? 'SSE' : 'Streamable HTTP'}
        </Text>
      </DetailRow>

      {/* Sharing mode is fixed at creation — shown read-only, no switch. */}
      <DetailRow label="Credential sharing">
        <Text variant="text-sm" color="primary">
          {isIndividual
            ? 'Individual — each user connects with their own credentials'
            : 'Shared — one credential set for the whole project'}
        </Text>
      </DetailRow>

      <DetailRow label="Credentials">
        {headerNames.length === 0 ? (
          <Text variant="text-sm" color="tertiary">
            No custom headers configured
          </Text>
        ) : (
          <div className="flex flex-col gap-[4px]">
            {headerNames.map((name) => (
              <div key={name} className="flex items-center gap-[8px]">
                <Text variant="text-xs" color="primary" className="font-mono">
                  {name}
                </Text>
                <Text variant="text-xs" color="quaternary" className="font-mono">
                  {MASKED_SECRET}
                </Text>
              </div>
            ))}
          </div>
        )}
      </DetailRow>

      {server.last_checked_at && (
        <DetailRow label="Last checked">
          <Text variant="text-sm" color="secondary">
            {formatDate(server.last_checked_at)}
          </Text>
        </DetailRow>
      )}

      {server.tools.length > 0 && (
        <DetailRow label={`Tools from last successful test (${server.tools.length})`}>
          <div className="flex flex-col gap-[4px] max-h-[220px] overflow-y-auto modern-scrollbar-spaced">
            {server.tools.map((tool) => (
              <div
                key={tool.name}
                className="flex flex-col bg-bg-secondary rounded-[var(--radius-sm,6px)] px-[8px] py-[6px]"
              >
                <Text variant="text-xs" weight="medium" color="primary" className="font-mono">
                  {tool.name}
                </Text>
                {tool.description && (
                  <Text variant="text-xs" color="tertiary" className="break-words line-clamp-2">
                    {tool.description}
                  </Text>
                )}
              </div>
            ))}
          </div>
        </DetailRow>
      )}

      {/* Client-credentials popup — shown when the provider has no automatic
          registration (DCR), so a pre-registered client ID + secret is needed.
          Portaled to <body>: the slide-over panel animates with a CSS transform,
          which would otherwise trap this fixed overlay inside the panel. */}
      {credsOpen &&
        createPortal(
          <Modal
            isOpen={credsOpen}
            onClose={() => !credsSaving && setCredsOpen(false)}
            title="Enter client credentials"
            size="sm"
            onCancel={() => setCredsOpen(false)}
            onSubmit={submitClientCredentials}
            submitLabel={credsSaving ? 'Saving…' : 'Save & sign in'}
            submitDisabled={credsSaving || !credsClientId.trim()}
            submitLoading={credsSaving}
          >
            <div className="flex flex-col gap-bg-v2-lg">
              <Text variant="text-xs" color="secondary">
                This server doesn&apos;t support automatic app registration. Enter the client ID
                (and secret, if you were given one) you registered with the provider.
              </Text>
              <Input
                label="Client ID"
                type="text"
                value={credsClientId}
                onChange={(e) => setCredsClientId(e.target.value)}
                placeholder="Client ID"
                className="font-mono"
                autoFocus
              />
              <Input
                label="Client secret (optional)"
                type="password"
                value={credsClientSecret}
                onChange={(e) => setCredsClientSecret(e.target.value)}
                placeholder="Client secret"
              />
              <Input
                label="Scopes (optional)"
                type="text"
                value={credsScopes}
                onChange={(e) => setCredsScopes(e.target.value)}
                placeholder="Space-separated — leave blank to request all the server offers"
                className="font-mono"
              />
              <Text variant="text-xs" color="tertiary">
                Leave blank and we&apos;ll request every scope the server advertises. Enter specific
                scopes to limit access.
              </Text>
              {credsError && <FormAlert message={credsError} variant="error" />}
            </div>
          </Modal>,
          document.body
        )}

      {/* Enable/disable — right aligned, after the tool list */}
      <IfHasPermission permissions={project?.permissions} requires={Permissions.UpdateProjectPermission}>
        <div className="flex items-center justify-end gap-[var(--spacing-md,8px)] pt-[var(--spacing-md,8px)] border-t border-border-secondary">
          <Text variant="text-xs" color="secondary">
            {server.enabled ? 'Enabled' : 'Disabled'}
          </Text>
          <EnableToggle enabled={server.enabled} disabled={mutationsLocked} onToggle={onToggle} />

        </div>
      </IfHasPermission>
    </div>
  );
};

export default ServerDetailsView;
